import type { Order } from '../types'

const STORAGE_PREFIX = 'wortelogy-order-alerted:'

function storageKey(placeId: string): string {
  return `${STORAGE_PREFIX}${placeId}`
}

function loadAlertedIds(placeId: string): Set<string> {
  if (!placeId || typeof localStorage === 'undefined') return new Set()
  try {
    const raw = localStorage.getItem(storageKey(placeId))
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((id): id is string => typeof id === 'string'))
  } catch {
    return new Set()
  }
}

function saveAlertedIds(placeId: string, ids: Set<string>): void {
  if (!placeId || typeof localStorage === 'undefined') return
  try {
    if (ids.size === 0) {
      localStorage.removeItem(storageKey(placeId))
      return
    }
    localStorage.setItem(storageKey(placeId), JSON.stringify([...ids]))
  } catch {
    // ignore quota / private mode
  }
}

/** Mark order IDs as already alerted (no repeat chime/notification). */
export function markOrdersAlerted(placeId: string, orderIds: readonly string[]): void {
  if (!placeId || orderIds.length === 0) return
  const next = loadAlertedIds(placeId)
  for (const id of orderIds) next.add(id)
  saveAlertedIds(placeId, next)
}

/** Return orders that have not yet been alerted for this place. */
export function filterUnalertedOrders(placeId: string, orders: Order[]): Order[] {
  if (!placeId || orders.length === 0) return orders
  const alerted = loadAlertedIds(placeId)
  return orders.filter((o) => !alerted.has(o.id))
}

/**
 * Seed alerted IDs with current pending orders so reopening the app does not re-alert.
 * Call after first cache load — does not play any sound.
 */
export function seedAlertedPendingOrders(placeId: string, orders: Order[]): void {
  if (!placeId) return
  const pendingIds = orders.filter((o) => o.status === 'pending').map((o) => o.id)
  if (pendingIds.length === 0) return
  markOrdersAlerted(placeId, pendingIds)
}

/** Drop alerted IDs for orders that are no longer pending. */
export function pruneAlertedOrders(placeId: string, orders: Order[]): void {
  if (!placeId) return
  const pendingIds = new Set(
    orders.filter((o) => o.status === 'pending').map((o) => o.id),
  )
  const alerted = loadAlertedIds(placeId)
  let changed = false
  for (const id of [...alerted]) {
    if (!pendingIds.has(id)) {
      alerted.delete(id)
      changed = true
    }
  }
  if (changed) saveAlertedIds(placeId, alerted)
}

/** Test helper — reset alerted state for a place. */
export function clearAlertedOrdersForPlace(placeId: string): void {
  if (!placeId || typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(storageKey(placeId))
  } catch {
    // ignore
  }
}
